import { useMemo } from 'react';
import { Box } from '@mui/material';
import { resolvePreviewHtmlFontSizePx, sanitizePreviewHtml } from '@/helpers/preview/sanitize-preview-html.helper';
import { DEFAULT_FONT_SIZE_CQH } from '@/helpers/preview/font-size-cqh.helper';

export interface PreviewScaledHtmlLayerProps {
  html: string;
  textColor?: string;
  /** Absolute cqh factor from event settings (default 2 = standard / UI 1.0×). */
  fontSizeMultiplier?: number;
  /** previewFrameHeight / browser viewportHeight — from the full preview frame */
  stageViewportScale?: number;
  /**
   * Layer box height in px (stageHeight × position.h/100).
   * Font size tracks this so preview matches player layer-relative `cqh`.
   */
  layerHeightPx?: number;
  /** When true, override baked inline colors so live settings.textColor is visible. */
  forceTextColor?: boolean;
}

/**
 * Composite HTML with viewport compensation: inner canvas is sized to the browser
 * viewport, then scaled into the layer box (vh/vw in templates match the player).
 * Font size uses layer box height (player layer-wrapper cqh parity).
 */
export function PreviewScaledHtmlLayer({
  html,
  textColor,
  fontSizeMultiplier = DEFAULT_FONT_SIZE_CQH,
  stageViewportScale = 1,
  layerHeightPx = 0,
  forceTextColor = false,
}: PreviewScaledHtmlLayerProps) {
  const sanitizedHtml = useMemo(() => sanitizePreviewHtml(html), [html]);

  if (!sanitizedHtml) {
    return null;
  }

  const scale = stageViewportScale > 0 ? stageViewportScale : 1;
  // After CSS scale(scale), visual size = fontSizePx * scale. Choose fontSizePx so
  // visual size = layerHeightPx * (cqh/100), matching player `${cqh}cqh` on the layer box.
  const fontSizePx = layerHeightPx > 0 ? resolvePreviewHtmlFontSizePx(layerHeightPx / scale, fontSizeMultiplier) : 0;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          'width': `${100 / scale}%`,
          'height': `${100 / scale}%`,
          'transform': `scale(${scale})`,
          'transformOrigin': 'top left',
          'overflow': 'hidden',
          ...(textColor
            ? {
                color: textColor,
                ...(forceTextColor ? { '& *:not(img)': { color: 'inherit !important' } } : {}),
              }
            : { color: 'unset' }),
          'fontSize': fontSizePx > 0 ? `${fontSizePx}px` : undefined,
          'lineHeight': 1.2,
          '& img': { maxWidth: '100%', height: 'auto' },
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </Box>
  );
}
